/**
 * @fileoverview Provides functionality for finding and completing TODO comments across files in a specified directory. The module includes methods to locate TODOs, log their details, and can generate code completions for these TODOs using an AI model and a configurable generation function.
 */

import { Model } from '@iterator/shared-node/ai/llm_types'
import { FileData, getFolder, logFolderAndFiles } from './folder'
import { generateCodeWithStreamV2 } from './generate/generate_v2'
import { print_ai } from '@iterator/shared-node/util/logging'
import * as path from 'path'

export async function completeTodos(
  directory: string,
  model: Model,
  generateFunction: typeof generateCodeWithStreamV2,
  todoPhrase: string = '// TODO (awolf):',
  additionalPrompt: string = '',
  filePath?: string,
): Promise<void> {
  const folder = getFolder(directory)
  logFolderAndFiles(folder)
  const todos = getTodos(directory, todoPhrase, filePath)

  print_ai(`Found ${todos.length} files with todos in ${folder.path}\n`)
  for (const fileTodo of todos) {
    print_ai(`${fileTodo.file.path}`)
    for (const todo of fileTodo.todos) {
      print_ai(`  ${todo.trim()}`)
    }
    print_ai('')
  }

  const promptPrefix = additionalPrompt
    ? `${additionalPrompt}\n\nWith that guidance in mind, please complete the following TODOs:\n\n`
    : 'Please complete all of the following TODOs:\n\n'

  await generateFunction(
    directory,
    `${promptPrefix}${todos
      .map(
        (fileTodo) => `<file>
  <path>
  ${fileTodo.file.path}
  </path>
  
  <todos>
  ${fileTodo.todos.join('\n')}
  </todos>
</file>
  `,
      )
      .join('\n')}
`,
    model,
  )
}

type FileTodos = {
  file: FileData
  todos: string[]
}

export function getTodos(
  directory: string,
  todoPhrase: string = 'TODO (awolf):',
  filePath?: string,
): FileTodos[] {
  const folder = getFolder(directory, true)
  logFolderAndFiles(folder)
  const todoFiles = folder.files.filter((file) => {
    if (filePath) {
      const fileName = path.basename(filePath)
      return path.basename(file.path) === fileName && file.contents.includes(todoPhrase)
    }
    return file.contents.includes(todoPhrase)
  })

  const todos: FileTodos[] = []
  for (const file of todoFiles) {
    const lines = file.contents.split('\n')
    const fileTodos: string[] = []
    for (const line of lines) {
      if (line.includes(todoPhrase)) {
        const todoIndex = line.indexOf(todoPhrase)
        const todoContent = line.slice(todoIndex + todoPhrase.length)
        fileTodos.push(todoContent.trim())
      }
    }
    todos.push({ file, todos: fileTodos })
  }

  for (const subFolder of folder.folders) {
    const subTodos = getTodos(subFolder.path, todoPhrase, filePath)
    todos.push(...subTodos)
  }
  return todos
}
